import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Button, Table, Tag, Spin, Typography, Space, Divider, Input, Form, Modal, message, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { EditOutlined, ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { PurchaseOrder, PurchaseItem } from '../../types';

const { Title } = Typography;
const { Option } = Select;

const PurchaseOrderDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [items, setItems] = useState<PurchaseItem[]>([]);
  const [courierModalVisible, setCourierModalVisible] = useState(false);
  const [courierForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 獲取採購訂單詳情
        const orderData = await api.purchaseOrders.getById(id as string);
        setOrder(orderData as PurchaseOrder);

        // 獲取採購訂單項目
        const itemsData = await api.purchaseOrders.getItems(id as string);
        setItems(itemsData as PurchaseItem[]);
      } catch (error) {
        console.error('Error fetching purchase order details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // 更新快遞單號
  const handleUpdateCourier = async (values: any) => {
    setSubmitting(true);
    try {
      await api.purchaseOrders.updateCourier(id as string, values);
      message.success(t('common.success'));

      // 更新訂單數據
      const orderData = await api.purchaseOrders.getById(id as string);
      setOrder(orderData as PurchaseOrder);

      setCourierModalVisible(false);
    } catch (error) {
      console.error('Error updating courier number:', error);
      message.error(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // 打開快遞單號更新模態框
  const showCourierModal = () => {
    // 確保 order 不為 null
    if (order) {
      courierForm.setFieldsValue({
        courier_number: order.courier_number,
        courier_status: order.courier_status,
      });
      setCourierModalVisible(true);
    }
  };

  // 採購項目表格列
  const itemColumns = [
    {
      title: t('purchaseOrders.productName'),
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: t('purchaseOrders.price'),
      dataIndex: 'price',
      key: 'price',
    },
    {
      title: t('purchaseOrders.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: t('purchaseOrders.subtotal'),
      dataIndex: 'subtotal',
      key: 'subtotal',
    },
  ];

  if (loading) {
    return <Spin size="large" />;
  }

  if (!order) {
    return <div>{t('common.noData')}</div>;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/purchase-orders')}
        >
          {t('common.back')}
        </Button>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/purchase-orders/${id}/edit`)}
        >
          {t('common.edit')}
        </Button>
      </Space>

      <Title level={2}>{t('purchaseOrders.title')} - {order.order_id}</Title>

      <Card style={{ marginBottom: 24 }}>
        <Descriptions title={t('purchaseOrders.title')} bordered>
          <Descriptions.Item label={t('purchaseOrders.orderId')}>{order.order_id}</Descriptions.Item>
          <Descriptions.Item label={t('purchaseOrders.storeId')}>{order.store_id}</Descriptions.Item>
          <Descriptions.Item label={t('purchaseOrders.platform')}>{order.platform}</Descriptions.Item>
          <Descriptions.Item label={t('purchaseOrders.purchaseDate')}>
            {typeof order.purchase_date === 'string'
              ? order.purchase_date
              : order.purchase_date.format('YYYY-MM-DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('purchaseOrders.contact')}>{order.contact}</Descriptions.Item>
          <Descriptions.Item label={t('purchaseOrders.totalAmount')}>{order.total_amount}</Descriptions.Item>
          <Descriptions.Item label={t('purchaseOrders.courierNumber')}>
            {order.courier_number || '-'}
            <Button
              type="link"
              size="small"
              onClick={showCourierModal}
              style={{ marginLeft: 8 }}
            >
              {t('purchaseOrders.updateCourier')}
            </Button>
          </Descriptions.Item>
          <Descriptions.Item label={t('purchaseOrders.courierStatus')}>
            <Tag color={order.courier_status === '已填寫' ? 'success' : 'warning'}>
              {order.courier_status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('purchaseOrders.createdAt')}>{order.created_at}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Divider orientation="left">{t('purchaseOrders.items')}</Divider>

      <Table
        columns={itemColumns}
        dataSource={items}
        rowKey="id"
        pagination={false}
        summary={(pageData) => {
          let totalQuantity = 0;
          let totalAmount = 0;

          pageData.forEach(({ quantity, subtotal }) => {
            totalQuantity += quantity;
            totalAmount += subtotal;
          });

          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}><strong>{t('common.total')}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={1}></Table.Summary.Cell>
              <Table.Summary.Cell index={2}><strong>{totalQuantity}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={3}><strong>{totalAmount}</strong></Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />

      {/* 快遞單號更新模態框 */}
      <Modal
        title={t('purchaseOrders.updateCourier')}
        visible={courierModalVisible}
        onCancel={() => setCourierModalVisible(false)}
        footer={null}
      >
        <Form
          form={courierForm}
          layout="vertical"
          onFinish={handleUpdateCourier}
        >
          <Form.Item
            name="courier_number"
            label={t('purchaseOrders.courierNumber')}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="courier_status"
            label={t('purchaseOrders.courierStatus')}
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="已填寫">{t('purchaseOrders.courierStatuses.filled')}</Option>
              <Option value="未填寫">{t('purchaseOrders.courierStatuses.unfilled')}</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              icon={<SaveOutlined />}
            >
              {t('common.save')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PurchaseOrderDetail;
